import React, { useState, useEffect } from 'react';
import { MAP_REGISTRY, getMapDisplayName, getMapDescription } from '../maps_generated/risk/map-registry.js';
import AddressSelector from './AddressSelector';
import { AddressDisplayLong } from './AddressDisplay';
import { NetworkErrorMessage } from './basics/NetworkErrorMessage';
import './GameMaker.css';

const MakeOfferForm = ({ 
  queriedGames, 
  setQueriedGames, 
  provider,
  signer,
  ethersUtils,
  createContract, 
  makeOffer, 
  GameRegistryConfig, 
  GameRegistryContractAddress,
  gameUrls,
  setGameUrls,
  validateNetwork,
  validateSigner,
  hideWrapper = false // New prop to hide the collapsible wrapper
 }) => {

  const [selectedGame, setSelectedGame] = useState(null); // Track which game is expanded
  const [formValues, setFormValues] = useState({}); // Store form values for each game
  const [isCollapsed, setIsCollapsed] = useState(false); // Track if entire section is collapsed
  const [connectionError, setConnectionError] = useState(null);
  const [networkValidation, setNetworkValidation] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState({}); // Track transaction status per game
  const [userClosedGames, setUserClosedGames] = useState(new Set()); // Track games user explicitly closed

  // Initialize transaction status to 'idle' for all games to prevent undefined status
  useEffect(() => {
    if (queriedGames.length > 0) {
      const initialStatus = {};
      queriedGames.forEach(game => {
        if (!transactionStatus[game.name]) {
          initialStatus[game.name] = { status: 'idle' };
        }
      });
      if (Object.keys(initialStatus).length > 0) {
        setTransactionStatus(prev => ({ ...prev, ...initialStatus }));
      }
    }
  }, [queriedGames]);


  // Listen for Playwright test events to synchronize form state
  useEffect(() => {
    const handlePlaywrightFormUpdate = (event) => {
      const { gameType, formValues: testFormValues } = event.detail;

      // Update formValues state to match what Playwright filled
      setFormValues(prev => ({
        ...prev,
        [gameType]: testFormValues
      }));

      // Set selectedGame to make the form visible and clear any user-closed flag
      setSelectedGame(gameType);
      setUserClosedGames(prev => {
        const newSet = new Set(prev);
        newSet.delete(gameType);
        return newSet;
      });
    };

    document.addEventListener('playwright-form-values-update', handlePlaywrightFormUpdate);
    
    return () => {
      document.removeEventListener('playwright-form-values-update', handlePlaywrightFormUpdate);
    };
  }, []);

  const handleGameClick = (gameName) => {
    if (selectedGame === gameName) {
      setSelectedGame(null); // Collapse the box
      // Track that user explicitly closed this game to prevent auto-reopening
      setUserClosedGames(prev => new Set([...prev, gameName]));
    } else {
      setSelectedGame(gameName); // Expand the clicked game box
      // Clear the userClosedGames flag since user is explicitly opening this game
      setUserClosedGames(prev => {
        const newSet = new Set(prev);
        newSet.delete(gameName);
        return newSet;
      });
      if (!formValues[gameName]) {
        // Initialize form values for the newly expanded game if not already set
        setFormValues((prev) => ({
          ...prev,
          [gameName]: {
            player1Bet: '0.001',
            player2Bet: '0.001',
            timeout: '600',
            gameParams: ['TicTacToe'].includes(gameName) || gameName.includes('Mill') || gameName.startsWith('Risk') ? '2' : '', // Default Random start (2) for games with "who starts" parameter
            targetPlayer: '', // target player address for personal offers
          },
        }));
      }
    }
  };

  const queryGames = async () => {
    if (!provider) {
      return;
    }
    
    // Validate network connection (skip signer validation for view function)
    try {
      const networkCheck = await validateNetwork();

      if (!networkCheck.isValid) {
        setConnectionError(networkCheck.error);
        setNetworkValidation(networkCheck);
        return;
      }

      // Clear any previous errors
      setConnectionError(null);
      setNetworkValidation(null);

      // For view functions like getAllGameNames(), explicitly specify we don't need a signer
      // This ensures the contract uses provider instead of signer for Custom RPC wallets
      const gameRegistry = await createContract(GameRegistryContractAddress, GameRegistryConfig.abi, false);
      const gameNames = await gameRegistry.getAllGameNames();
      
      const games = [];
      const newGameUrls = {};
      for (let i = 0; i < gameNames.length; i++) {
        try {
          const game = await gameRegistry.getGameInfo(gameNames[i]);

          // Extract only the named properties, not the numeric indices
          const gameObject = {
            name: gameNames[i],
            gameAddress: game.gameAddress,
            paramCount: game.paramCount,
            description_game: game.description_game,
            description_params: game.description_params,
            url: game.url
          };

          games.push(gameObject);
          newGameUrls[gameNames[i]] = game.url;
        } catch (gameError) {
          console.error(`❌ Error processing game ${gameNames[i]}:`, gameError);
        }
      }

      // Only update state if the content has actually changed (prevent re-render loops)
      const gamesChanged = JSON.stringify(games) !== JSON.stringify(queriedGames);
      const urlsChanged = JSON.stringify(newGameUrls) !== JSON.stringify(gameUrls);

      if (gamesChanged) {
        console.log('📋 Games data changed, updating queriedGames');
        setQueriedGames(games);
      }

      if (urlsChanged) {
        console.log('🔗 Game URLs changed, updating gameUrls');
        setGameUrls(newGameUrls);
      }
    } catch (error) {
      console.error('❌ Error loading game templates:', error);

      // Provide more specific error messages for common issues
      let errorMessage = error.message;
      if (error.message.includes('GameRegistry contract not available')) {
        errorMessage = 'Network mismatch: Game templates not available on current network. Please check your wallet network settings.';
      } else if (error.message.includes('call revert exception')) {
        errorMessage = 'Contract call failed: Unable to connect to GameRegistry. Please check your network connection and try a different RPC endpoint.';
      } else if (error.message.includes('provider')) {
        errorMessage = 'Connection error: Unable to connect to blockchain. Please refresh the page or check your wallet connection.';
      }

      setConnectionError(`Failed to load game templates: ${errorMessage}`);
    }
  };

  useEffect(() => {
    if (provider && signer && GameRegistryContractAddress) {
      queryGames();
    }
  }, [provider, signer, GameRegistryContractAddress]);

  const handleInputChange = (gameName, field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [gameName]: {
        ...prev[gameName],
        [field]: value,
      },
    }));
  };

  const handleMakeOffer = async () => {
    const game = queriedGames.find(g => g.name === selectedGame); // Get the correct game object
    if (game) {
      const { player1Bet, player2Bet, timeout, gameParams, targetPlayer } = formValues[game.name]; // Extract the parameters from form values
      
      // Simple logic: empty targetPlayer = open offer, non-empty = personal offer
      const targetAddress = targetPlayer && targetPlayer.trim() !== '' ? targetPlayer : '';
      
      // Set transaction status to pending and collapse the form
      setTransactionStatus(prev => ({
        ...prev,
        [game.name]: { status: 'pending', message: 'Transaction pending' }
      }));
      setSelectedGame(null); // Collapse the form
      
      try {
        // Call makeOffer and wait for completion
        await makeOffer(game, player1Bet, player2Bet, timeout, gameParams, targetAddress);
        
        // Set success status
        setTransactionStatus(prev => ({
          ...prev,
          [game.name]: { status: 'success', message: 'Offer created' }
        }));
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setTransactionStatus(prev => ({
            ...prev,
            [game.name]: { status: 'idle' }
          }));
        }, 5000);
        
      } catch (error) {
        console.error("Transaction failed:", error);
        setTransactionStatus(prev => ({
          ...prev,
          [game.name]: { status: 'error', message: 'Transaction failed - click to retry' }
        }));
      }
    } else {
      console.error("No game selected or game not found.");
    }
  };

  // Main content component
  const renderContent = () => (
    <div className="space-y-4">
      {connectionError && (
        <NetworkErrorMessage 
          message={connectionError}
          dismiss={() => setConnectionError(null)}
          validation={networkValidation}
          onRetry={() => queryGames()}
        />
      )}
      
      {queriedGames.map((game) => {
        const txStatus = transactionStatus[game.name];
        const isPending = txStatus?.status === 'pending';
        const isSuccess = txStatus?.status === 'success';
        const isError = txStatus?.status === 'error';
        const showStatus = txStatus && txStatus.status && txStatus.status !== 'idle';

        return (
          <div 
            key={game.gameAddress}
            className={`border rounded-lg shadow-md transition ${
              isPending ? 'bg-blue-100 border-blue-300 pulse-blue' :
              isSuccess ? 'bg-green-100 border-green-300' :
              isError ? 'bg-red-100 border-red-300' :
              selectedGame === game.name ? 'bg-gray-200' : 'bg-white'
            } ${showStatus ? 'p-6' : 'p-4'}`}
            style={{ position: 'relative' }}
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => {
                // Clear any transaction status first to reset to normal behavior
                if (isPending || isSuccess || isError) {
                  setTransactionStatus(prev => ({ ...prev, [game.name]: { status: 'idle' } }));
                  // Don't toggle when clearing transaction status, just clear and return
                  return;
                }
                // Always allow toggling the collapsible behavior
                handleGameClick(game.name);
              }}
            >
              <div className="text-lg font-bold">{game.name}</div>
              {showStatus ? (
                <div className={`text-sm font-medium ${
                  isPending ? 'text-blue-600' :
                  isSuccess ? 'text-green-600' :
                  'text-red-600'
                }`}>
                  {txStatus.message}
                </div>
              ) : (
                <div className="text-sm text-gray-500">{selectedGame === game.name ? '-' : '+'}</div>
              )}
            </div>
            
            {/* Show form content when game is selected and not showing status */}
            {selectedGame === game.name && formValues[game.name] && !showStatus && (
              <div className="mt-4 space-y-4 text-sm" onClick={(e) => e.stopPropagation()}>
                <div><strong>Address:</strong> <AddressDisplayLong address={game.gameAddress} useExplorerLink={true} /></div>
                <div><strong>Description:</strong> {game.description_game}</div>
                {game.paramCount > 0 && (
                  <>
                    <div><strong>Parameter Count:</strong> {game.paramCount.toString()}</div>
                    <div><strong>Parameter Descriptions:</strong> {game.description_params}</div>
                  </>
                )}
                <div><strong>URL:</strong> <a href={game.url} target="_blank" rel="noopener noreferrer">{game.url}</a></div>

                <div className="space-y-4 mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm text-gray-700">Player 1 Bet (in POL):</label>
                    <input 
                      type="number" 
                      value={formValues[game.name].player1Bet} 
                      onChange={(e) => handleInputChange(game.name, 'player1Bet', e.target.value)} 
                      className="border rounded-md p-1 w-1/2"
                    />
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm text-gray-700">Player 2 Bet (in POL):</label>
                    <input 
                      type="number" 
                      value={formValues[game.name].player2Bet} 
                      onChange={(e) => handleInputChange(game.name, 'player2Bet', e.target.value)} 
                      className="border rounded-md p-1 w-1/2"
                    />
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm text-gray-700">Timeout (in minutes):</label>
                    <input 
                      type="number" 
                      value={formValues[game.name].timeout} 
                      onChange={(e) => handleInputChange(game.name, 'timeout', e.target.value)} 
                      className="border rounded-md p-1 w-1/2"
                    />
                  </div>

                  {/* Simplified Targeting */}
                  <div className="mb-4">
                    <AddressSelector
                      selectedAddress={formValues[game.name].targetPlayer}
                      onAddressChange={(address) => handleInputChange(game.name, 'targetPlayer', address)}
                      label="Personal Offer To (optional)"
                      placeholder="Leave empty for open offer, or enter/select specific player"
                    />
                    <div className="mt-1 text-xs text-gray-500">
                      Empty = Open to all players • Valid address = Personal offer to specific player
                    </div>
                  </div>
                  {game.paramCount > 0 && (
                    (['TicTacToe'].includes(game.name) || game.name.includes('Mill') || game.name.startsWith('Risk')) ? (
                      <div className="mb-4">
                        <label className="block text-sm text-gray-700 mb-2">Who starts first:</label>
                        <div className="grid grid-cols-1 gap-2">
                          <div 
                            className={`border rounded-lg p-3 cursor-pointer transition ${
                              formValues[game.name].gameParams === '0' 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            onClick={() => handleInputChange(game.name, 'gameParams', '0')}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-bold text-blue-600">
                                  1
                                </div>
                              </div>
                              <div className="flex-grow">
                                <h4 className="text-sm font-medium text-gray-900">Player 1 starts</h4>
                                <p className="text-xs text-gray-500">The offer creator goes first</p>
                              </div>
                              <div className="flex-shrink-0">
                                {formValues[game.name].gameParams === '0' && (
                                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div 
                            className={`border rounded-lg p-3 cursor-pointer transition ${
                              formValues[game.name].gameParams === '1' 
                                ? 'border-red-500 bg-red-50' 
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            onClick={() => handleInputChange(game.name, 'gameParams', '1')}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-sm font-bold text-red-600">
                                  2
                                </div>
                              </div>
                              <div className="flex-grow">
                                <h4 className="text-sm font-medium text-gray-900">Player 2 starts</h4>
                                <p className="text-xs text-gray-500">The player who accepts the offer goes first</p>
                              </div>
                              <div className="flex-shrink-0">
                                {formValues[game.name].gameParams === '1' && (
                                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div 
                            className={`border rounded-lg p-3 cursor-pointer transition ${
                              formValues[game.name].gameParams === '2' 
                                ? 'border-green-500 bg-green-50' 
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            onClick={() => handleInputChange(game.name, 'gameParams', '2')}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-sm font-bold text-green-600">
                                  ?
                                </div>
                              </div>
                              <div className="flex-grow">
                                <h4 className="text-sm font-medium text-gray-900">Random</h4>
                                <p className="text-xs text-gray-500">Randomly determined when the game starts</p>
                              </div>
                              <div className="flex-shrink-0">
                                {formValues[game.name].gameParams === '2' && (
                                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm text-gray-700">Game Parameters (optional):</label>
                        <input 
                          type="text" 
                          value={formValues[game.name].gameParams} 
                          onChange={(e) => handleInputChange(game.name, 'gameParams', e.target.value)} 
                          className="border rounded-md p-1 w-1/2"
                          placeholder="Leave empty for most games"
                        />
                      </div>
                    )
                  )}
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // Ensure the click is captured by the button and doesn't collapse the box
                      handleMakeOffer();
                    }}
                    className="px-6 py-2 rounded-md text-sm bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
                  >
                    Make Offer
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Return with or without wrapper based on hideWrapper prop
  if (hideWrapper) {
    return renderContent();
  }

  return (
    <div className={`collapsible-box ${isCollapsed ? 'collapsed' : 'expanded'}`}>
      <div className="collapsible-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="text-lg font-bold">Offer Template</div>
        <div className="text-center mt-2 text-gray-500 text-sm">
          {isCollapsed ? '▼ Click to expand' : '▲ Click to collapse'}
        </div>
      </div>

      {!isCollapsed && (
        <div className="collapsible-content p-4 mt-4">
          {renderContent()}
        </div>
      )}
    </div>
  );
};

export default MakeOfferForm;
